import type { catalogSearchFr, connectionsFr, lineageFr, previewFr, qualityFr } from './donnees.fr';

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

/** English texts for lot L07 (data connections, lineage, quality, step preview, catalog search). Same keys as `donnees.fr.ts`. */
export const connectionsEn: DeepStrings<typeof connectionsFr> = {
  title: 'Data connections',
  subtitle:
    'The databases, SFTP servers, APIs, HDFS and buckets that project {{project}} reads and writes. No password here: each connection names a secret.',
  searchPlaceholder: 'Search a connection…',
  new: 'New connection',
  newTitle: 'New connection',
  newSubtitle:
    'Fields follow the type. The secret must be bound to the project and to the destination (host:port): pick one, or create it here with both bindings.',
  created: 'Connection {{name}} created.',
  createdWithSecret: 'Secret {{secret}} created and bound to {{host}}.',
  saved: 'Connection saved.',
  empty: {
    title: 'No connection',
    description:
      'Declare a database, an SFTP server, an API or a bucket to test it, explore it and read it from a DAG.',
  },
  columns: {
    name: 'Name',
    type: 'Type',
    destination: 'Destination',
    secret: 'Secret',
    owner: 'Owner',
    updated: 'Updated',
  },
  types: {
    postgresql: 'PostgreSQL',
    mysql: 'MySQL',
    mariadb: 'MariaDB',
    sqlserver: 'SQL Server',
    oracle: 'Oracle',
    s3: 'Bucket (S3)',
    sftp: 'SFTP',
    http: 'REST API',
    hdfs: 'HDFS',
  },
  sslModes: {
    default: 'Driver default',
    disable: 'No encryption',
    require: 'Encrypted, certificate not verified',
    verify: 'Encrypted and verified',
  },
  authTypes: {
    none: 'None',
    bearer: 'Bearer token',
    basic: 'Basic (user and password)',
    header: 'Custom header',
  },
  secretModes: {
    none: 'No secret',
    existing: 'Pick a project secret',
    new: 'Create the secret here',
  },
  fields: {
    name: 'Name',
    nameHelp: 'Lowercase letters, digits, dashes and underscores: the connection’s handle in DAGs.',
    type: 'Type',
    host: 'Host',
    port: 'Port',
    portHelp: 'Empty: the type’s default port (5432, 3306, 1433, 1521, 22, 9870).',
    database: 'Database',
    databaseHelp: 'Oracle: the service name.',
    schema: 'Schema',
    username: 'User',
    sslMode: 'Encryption',
    sslModeHelp: 'Oracle only accepts “no encryption”.',
    rpcPort: 'RPC port',
    rpcPortHelp: 'Used by Spark (default: 8020).',
    hostKey: 'Host key fingerprint',
    hostKeyHelp:
      'OpenSSH SHA256 fingerprint. Empty: the test reports the server’s one without authenticating, and you pin it.',
    bucket: 'Bucket',
    baseUrl: 'Base URL',
    baseUrlHelp: 'http(s)://host[:port], without a path.',
    authType: 'Authentication',
    authHeader: 'Header name',
    path: 'Path',
    pathHelp: 'Base folder or prefix; for an API, the default preview path.',
    secretMode: 'Secret',
    secretModeHelp: 'Password, key or token: never typed into the connection, always kept in a secret.',
    secretId: 'Project secret',
    secretIdHelp:
      'Only secrets bound to this project are offered; their destination must be the connection’s.',
    secretName: 'New secret name',
    secretNameHelp: 'Letters, digits, “_”, “.” and “-”. It will be bound to the project and the destination.',
    secretValue: 'Secret value',
    secretValueHelp: 'Sent once, sealed by the platform, never shown again.',
    description: 'Description',
  },
  errors: {
    name: 'Lowercase letters, digits, “-” and “_”, 63 characters at most.',
    baseUrl: 'An http(s)://host[:port] URL, without a path.',
    secretHost: 'This secret is bound to {{host}}, the connection targets {{destination}}.',
    secretName: 'A secret name: letters, digits, “_”, “.” and “-”.',
    noDestination: 'Fill in the host (or URL) first: the secret is bound to that destination.',
  },
  detail: {
    tabs: {
      summary: 'Summary',
      test: 'Test',
      explore: 'Explore',
      definition: 'Definition',
    },
    destination: 'Destination',
    noSecret: 'No secret',
    hostKeyMissing: 'No pinned fingerprint',
    delete: 'Delete connection',
    confirmDelete:
      'Delete connection {{name}}? DAGs that copied its fields do not change; the secret is not deleted.',
    deleted: 'Connection {{name}} deleted.',
  },
  test: {
    hint: 'The platform connects from graal-api with the bound secret, and reads nothing.',
    status: 'Result',
    run: 'Test the connection',
    running: 'Testing…',
    ok: 'Reachable',
    failed: 'Unreachable',
    latency: 'Latency',
    latencyValue: '{{ms}} ms',
    server: 'Server',
    message: 'Answer',
    observedKey: 'Fingerprint presented by the server',
    pinHint:
      'No fingerprint is pinned: the test stopped after the key exchange, without authenticating. Check this fingerprint with the server’s administrator before pinning it.',
    mismatch: 'The presented fingerprint does NOT match the pinned one ({{pinned}}).',
    pin: 'Pin this fingerprint',
    pinned: 'Fingerprint pinned. Run the test again to authenticate.',
    idle: 'The last test result shows here.',
  },
  explore: {
    title: 'Objects',
    root: 'Root',
    up: 'Parent folder',
    columns: {
      name: 'Name',
      kind: 'Kind',
      size: 'Size',
    },
    kinds: {
      table: 'Table',
      view: 'View',
      file: 'File',
      directory: 'Folder',
    },
    empty: { title: 'Nothing here', description: 'The connection returns no object at this place.' },
    schema: 'Columns of {{object}}',
    schemaColumns: { name: 'Column', type: 'Type', nullable: 'Nullable' },
    preview: 'First rows',
    previewMeta_one: '{{count}} row · {{ms}} ms',
    previewMeta_other: '{{count}} rows · {{ms}} ms',
    truncated: 'Cut at {{limit}} rows',
    format: 'Format: {{format}}',
    noRows: { title: 'No row', description: 'The object holds no readable row.' },
    back: 'Back to the list',
  },
};

export const lineageEn: DeepStrings<typeof lineageFr> = {
  title: 'Lineage',
  subtitle:
    'The datasets read and written by the runs of {{tenant}}, read back from their OpenLineage events. Open one to see where it comes from and where it goes.',
  searchPlaceholder: 'Namespace, name or field…',
  serverSearch: 'Search the lineage',
  columns: {
    dataset: 'Dataset',
    namespace: 'Namespace',
    fields: 'Fields',
    quality: 'Quality',
    lastSeen: 'Seen',
    job: 'Last job',
  },
  fieldsCount_one: '{{count}} field',
  fieldsCount_other: '{{count}} fields',
  empty: {
    title: 'No dataset',
    description: 'The lineage fills up when a run emits its OpenLineage events (low-code job, Spark).',
  },
  graph: {
    title: 'Lineage of {{name}}',
    subtitle: 'Datasets and jobs linked to {{namespace}}, {{depth}} hops each way.',
    depth: 'Depth',
    back: 'Lineage',
    canvas: 'Lineage graph',
    kinds: { dataset: 'Dataset', job: 'Job' },
    nodeCount: 'Datasets: {{datasets}} · jobs: {{jobs}}',
    columnsTitle: 'Column lineage',
    columns: {
      source: 'Source',
      target: 'Target',
      transformation: 'Transformation',
    },
    noColumns: {
      title: 'No column lineage',
      description: 'The runs of these jobs did not emit the columnLineage facet.',
    },
    openDataset: 'View this dataset',
    openJob: 'Open the job',
    notFound: 'No run ever read or wrote this dataset.',
  },
  run: {
    inputs: 'Datasets read',
    outputs: 'Datasets written',
    columns: { dataset: 'Dataset', namespace: 'Namespace', fields: 'Fields' },
    viewGraph: 'View the graph',
    events_one: '{{count}} OpenLineage event',
    events_other: '{{count}} OpenLineage events',
    empty: {
      title: 'No lineage event',
      description: 'This run emitted no OpenLineage event (GRAAL_OPENLINEAGE).',
    },
    noInputs: { title: 'No dataset read' },
    noOutputs: { title: 'No dataset written' },
  },
};

export const qualityEn: DeepStrings<typeof qualityFr> = {
  statuses: {
    passed: 'Passed',
    warning: 'Warning',
    failed: 'Failed',
    unknown: 'Not assessed',
  },
  severities: { error: 'Blocking', warning: 'Warning', info: 'Info' },
  label: 'Quality',
  run: {
    title: 'Quality checks',
    summary_one: '{{count}} failed check out of {{total}}',
    summary_other: '{{count}} failed checks out of {{total}}',
    created: 'Report of {{date}}',
    columns: {
      status: 'Result',
      check: 'Check',
      column: 'Column',
      severity: 'Severity',
      observed: 'Observed',
      expected: 'Expected',
      message: 'Message',
      task: 'Step',
    },
    checkStatuses: { passed: 'Passed', failed: 'Failed' },
    empty: {
      title: 'No quality report',
      description: 'This run did not execute a “Quality checks” task (GRAAL_QUALITY_REPORT).',
    },
  },
};

export const previewEn: DeepStrings<typeof previewFr> = {
  title: 'Step preview',
  hint: 'Launches an ephemeral run reduced to the step and its ancestors, on a sample; the job is not changed. A write step is previewed through what it would write.',
  fields: {
    task: 'DAG step',
    limit: 'Rows at most',
    limitHelp: 'From 1 to 500.',
  },
  run: 'Run the preview',
  statuses: {
    pending: 'Queued',
    running: 'Running',
    complete: 'Done',
    failed: 'Failed',
  },
  runId: 'Run {{id}}',
  result: 'Result of {{task}}',
  meta_one: '{{count}} row',
  meta_other: '{{count}} rows',
  truncated: 'Sample cut',
  failed: 'The preview failed: {{error}}',
  waiting: 'The preview run is starting; the result shows as soon as it is ready.',
  idle: { title: 'No preview', description: 'Pick a step, then run the preview.' },
  noDefinition: {
    title: 'No DAG',
    description: 'This low-code job has no definition: there is no step to preview.',
  },
  empty: { title: 'No row', description: 'The step yields no row on this sample.' },
};

export const catalogSearchEn: DeepStrings<typeof catalogSearchFr> = {
  placeholder: 'Search a table, a description, a tag, a term…',
  submit: 'Search',
  columnPlaceholder: 'Search by column name…',
  columnSubmit: 'Column',
  title: 'Tables found',
  meta_one: '{{count}} table for “{{search}}”',
  meta_other: '{{count}} tables for “{{search}}”',
  metaFacets_one: '{{count}} table for these filters',
  metaFacets_other: '{{count}} tables for these filters',
  metaTotal: '({{total}} in all, refine the search)',
  clear: 'Clear',
  includeDropped: 'Tables gone from the engine',
  facets: {
    all: '{{facet}}: all',
    option: '{{value}} ({{count}})',
    layer: 'Catalog',
    format: 'Format',
    tag: 'Tag',
    owner: 'Owner',
    certification: 'Certification',
    quality: 'Quality',
    classification: 'Classification',
  },
  columns: {
    table: 'Table',
    location: 'Catalog / database',
    owner: 'Owner',
    certification: 'Certification',
    tags: 'Tags',
    fields: 'Matched fields',
    quality: 'Quality',
  },
  empty: { title: 'No table', description: 'No readable table matches this search.' },
  viewLineage: 'View the lineage',
};
